/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { type ReactNode } from "react";
import { cn } from "../lib/utils";

/** The detail-page card: a titled section. Renders in place, no entrance choreography. */
export function Section({
  title,
  children,
  className,
}: {
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("border border-border bg-bg-elevated shadow-card rounded-3xl p-5 sm:p-6", className)}>
      {title && <h2 className="font-display text-lg text-text-primary">{title}</h2>}
      <div className={cn(title && "mt-3", "@container")}>{children}</div>
    </section>
  );
}

/** Label/value rows inside a Section. */
export type Fact = [string, ReactNode];

/** The facts worth a row: a null, undefined or empty value is not said. */
export function present(pairs: [string, ReactNode | null | undefined][]): Fact[] {
  return pairs.filter((pair): pair is Fact => pair[1] !== null && pair[1] !== undefined && pair[1] !== "");
}

/** Label/value rows: side by side, and one above the other only when their own box is narrow. */
export function Facts({ facts }: { facts: Fact[] }) {
  return (
    <div className="@container">
      <dl className="grid gap-y-1 text-sm @3xs:grid-cols-[minmax(5.5rem,max-content)_minmax(0,1fr)] @3xs:gap-x-4 @3xs:gap-y-1.5">
        {facts.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-text-tertiary">{label}</dt>
            <dd className="min-w-0 break-words text-text-primary @max-3xs:mb-1">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** A small caps caption over a section. */
export const EYEBROW = "text-[11px] font-medium uppercase tracking-[0.12em] text-text-tertiary";

/** A small heading that splits one Section into labelled groups of Facts. */
export function SubHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className={EYEBROW}>{children}</h3>
  );
}
